import { Box, Typography } from '@mui/material';
import { PlanCard, SectionLabel } from './components';
import { ProficiencySummaryTiles } from './ProficiencySummaryTiles';
import { COLORS } from './tokens';
import {
  proficiencySummary,
  reclassificationRows,
  type PlanStudent,
  type ReclassRow,
} from './derive';
import { useReclassCriteria } from './reclassCriteriaContext';
import { ExitDefinitionNote } from '../../components/ExitDefinitionNote';

const RECLASS_FLEX = { criterion: 2, score: 1, required: 1, status: 1 } as const;

function ReclassTable({ rows }: { rows: ReclassRow[] }) {
  const headers: Array<{ label: string; flex: number; align: 'left' | 'right' }> = [
    { label: 'CRITERION', flex: RECLASS_FLEX.criterion, align: 'left' },
    { label: 'SCORE', flex: RECLASS_FLEX.score, align: 'right' },
    { label: 'REQUIRED', flex: RECLASS_FLEX.required, align: 'right' },
    { label: 'STATUS', flex: RECLASS_FLEX.status, align: 'right' },
  ];
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, pb: 1, borderBottom: `1px solid ${COLORS.hairlineSoft}` }}>
        {headers.map((h) => (
          <Box key={h.label} sx={{ flex: h.flex, textAlign: h.align }}>
            <SectionLabel>{h.label}</SectionLabel>
          </Box>
        ))}
      </Box>
      {rows.map((row) => {
        const status = row.met ? 'Met' : row.gap != null ? `-${row.gap.toFixed(1)}` : '—';
        return (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              gap: 1,
              py: 1.25,
              borderBottom: `1px solid ${COLORS.hairlineSoft}`,
              alignItems: 'center',
            }}
          >
            <Box sx={{ flex: RECLASS_FLEX.criterion }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>
                {row.label.split(' · ')[0]}
              </Typography>
            </Box>
            <Box sx={{ flex: RECLASS_FLEX.score, textAlign: 'right' }}>
              <Typography sx={{ fontSize: 14, color: COLORS.ink }}>{row.current?.toFixed(1) ?? '—'}</Typography>
            </Box>
            <Box sx={{ flex: RECLASS_FLEX.required, textAlign: 'right' }}>
              <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{row.required.toFixed(1)}</Typography>
            </Box>
            <Box sx={{ flex: RECLASS_FLEX.status, textAlign: 'right' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: row.met ? '#2F7A1A' : '#B20101' }}>
                {status}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export function ProficiencyCard({ student, year }: { student: PlanStudent; year: number | null }) {
  const criteria = useReclassCriteria();
  const rows = reclassificationRows(student, criteria);

  return (
    <PlanCard
      title="Current Proficiency & Exit Projection"
      subtitle={`${criteria.assessment}, Spring ${year ?? ''} · ${criteria.state} exit criteria`}
    >
      <ExitDefinitionNote />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>Proficiency Summary</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.57, color: COLORS.ink }}>
          {proficiencySummary(student, criteria)}
        </Typography>
      </Box>

      <ProficiencySummaryTiles student={student} year={year} />

      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
            Exit Criteria
          </Typography>
          <ReclassTable rows={rows} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
            Exit Projection
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.57, color: COLORS.ink }}>
            Time-to-exit projections will be available with Spring 2027 ACCESS results. WIDA reset the
            ACCESS scale score for 2025–26 and is finalizing new proficiency-level cut scores. A second post-reset
            administration is needed before a multi-year projection can be generated.
          </Typography>
        </Box>
      </Box>
    </PlanCard>
  );
}
